import { useState, useCallback, useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { getCartItemsList, deleteCartItem } from "../../../store/actions";
import { useAppSelector } from "../../../store/hooks";
import { handleError, mapCartItemsToFundCodes } from "../../../utils/cart";
import { cartCheckout, getAmcMasters } from "../../../store/actions/cart";
import {
  RedemptionCartItem,
  SwitchCartItem,
  StpCartItem,
  Swp,
  AdditionalPurchaseCartItem,
  NewPurchaseCartItem,
  SipCartItem,
} from "./CartItems";
import {
  CartItems,
  CartItemsMap,
  Options,
  TransactionType,
} from "../../../store/types";

interface LocationState {
  pan: string;
}
export const MAXIMUM_CART_ITEMS = 5;

export interface FundItemsProps
  extends React.FC<{
    selected: Array<string>;
    fundCode: string;
    transactionType: keyof CartItemsMap;
    selectCartItem: ReturnType<typeof useInvestorCart>["selectCartItem"];
    removeCartItem: ReturnType<typeof useInvestorCart>["removeCartItem"];
    editCartItem: ReturnType<typeof useInvestorCart>["editCartItem"];
    fundCartItems: ReturnType<
      typeof useInvestorCart
    >["mappedCartItems"][string];
    newPurchaseSipType?: "SIPN";
  }> {}

export interface CartItemProps<T extends keyof CartItemsMap>
  extends React.FC<{
    cartItemId: string;
    cartId: string;
    isSelected: boolean;
    fundCode: string;
    selectCartItem: ReturnType<typeof useInvestorCart>["selectCartItem"];
    removeCartItem: ReturnType<typeof useInvestorCart>["removeCartItem"];
    editCartItem: ReturnType<typeof useInvestorCart>["editCartItem"];
    payload: CartItemsMap[T];
  }> {}

export const CART_ITEMS_MAP = (newPurchaseSipType?: "SIPN") => ({
  NPL: {
    amountKey: ["amount"],
    cartComponent: NewPurchaseCartItem,
    trName: "New Purchase",
    path: "/cart/edit-transaction/new-purchase",
    newTransactionPath: "/cart/add-transaction/new-purchase",
  },
  SINI: {
    amountKey: ["installmentAmount"],
    cartComponent: SipCartItem,
    trName: "SIP",
    path: "/cart/edit-transaction/sip-new",
    newTransactionPath: "/cart/add-transaction/sip-new",
  },
  SIPN: {
    amountKey: ["installmentAmount"],
    cartComponent: SipCartItem,
    trName: "SIP",
    path: "/cart/edit-transaction/sip-new",
    newTransactionPath: "/cart/add-transaction/sip-new",
  },

  ISIP: {
    amountKey: ["installmentAmount"],
    cartComponent: SipCartItem,
    trName: "SIP Existing",
    path: "/cart/edit-transaction/sip-existing",
    newTransactionPath: "/cart/add-transaction/sip-existing",
  },
  SIN: {
    amountKey: ["installmentAmount", "amount"],
    cartComponent: SipCartItem,
    trName: newPurchaseSipType === "SIPN" ? "SIP New Purchase" : "SIP Existing",
    path:
      newPurchaseSipType === "SIPN"
        ? "/cart/edit-transaction/sip-new"
        : "/cart/edit-transaction/sip-existing",
    newTransactionPath:
      newPurchaseSipType === "SIPN"
        ? "/cart/add-transaction/sip-new"
        : "/cart/add-transaction/sip-existing",
  },

  APL: {
    amountKey: ["amount"],
    cartComponent: AdditionalPurchaseCartItem,
    trName: "Additional Purchase",
    path: "/cart/edit-transaction/additional-purchase",
    newTransactionPath: "/cart/add-transaction/additional-purchase",
  },
  RED: {
    amountKey: ["amount"],
    cartComponent: RedemptionCartItem,
    trName: "Redemption",
    path: "/cart/edit-transaction/redemption",
    newTransactionPath: "/cart/add-transaction/redemption",
  },
  SWITCH: {
    amountKey: ["amount"],
    cartComponent: SwitchCartItem,
    trName: "Switch",
    path: "/cart/edit-transaction/switch",
    newTransactionPath: "/cart/add-transaction/switch",
  },
  STP: {
    amountKey: ["amount"],
    cartComponent: StpCartItem,
    trName: "STP",
    path: "/cart/edit-transaction/stp",
    newTransactionPath: "/cart/add-transaction/stp",
  },
  SWP: {
    amountKey: ["amount"],
    cartComponent: Swp,
    trName: "SWP",
    path: "/cart/edit-transaction/swp",
    newTransactionPath: "/cart/add-transaction/swp",
  },
  default: {
    amountKey: "",
    path: "/new-transaction",
    trName: "",
    cartComponent: "",
    newTransactionPath: "/new-transaction",
  },
});

const PAYMENT_OPTIONS_MAP = {
  APL: "/payment-options/additional-purchase/cart", //Additional Purchase
  SIN: "/payment-options/sip-purchase-with-payment/cart", //SIP Existing with Payment or SIP New with Payment
  ISIP: "/payment-options/sip-purchase-without-payment/cart", //SIP Existing without Payment
};

export default function useInvestorCart() {
  const dispatch = useDispatch();
  const history = useHistory();

  const location = useLocation<LocationState>();

  const cartItems: CartItems<TransactionType> = useAppSelector(
    (state) => state.cartReducer
  ).cartItems;

  const [selected, setSelected] = useState<string[]>(
    cartItems.map((item) => item.cartItemId)
  );

  const [funds, setFunds] = useState<Options>([]);

  const { token: arnCode } = useAppSelector((state) => state.authReducer);

  const { state } = location;
  const { pan } = state;

  const updateCart = useCallback(
    async function () {
      try {
        const requestBody = { arnCode, pan };
        const response = await getCartItemsList(requestBody)(dispatch);
        const cartItems = response.cartItems;
        if (!cartItems.length) {
          throw new Error("No items in the current investor's cart");
        }
        const cartIdList = cartItems.map((item) => item.cartItemId);
        setSelected(cartIdList);
      } catch (error) {
        handleError(error);
        history.replace("/new-transaction");
      }
    },
    [dispatch, history, pan, arnCode]
  );

  const removeCartItem = useCallback(
    async function (cartId: string, cartItemId: string) {
      try {
        const requestBody = { cartId, cartItemId, arnCode, pan };
        await deleteCartItem(requestBody)(dispatch);
        await updateCart();
        alert("Scheme deleted successfully");
        setSelected((prev) => {
          if (prev.includes(cartItemId)) {
            return prev.filter((id) => id !== cartItemId);
          }
          return prev;
        });
      } catch (error) {
        handleError(error);
        history.replace("/new-transaction");
      }
    },
    [arnCode, dispatch, updateCart, pan, history]
  );

  const selectCartItem = useCallback(function selectCartItem(
    cartItemId: string
  ) {
    setSelected((prevSelected) =>
      prevSelected.includes(cartItemId)
        ? prevSelected.filter((id) => id !== cartItemId)
        : [...prevSelected, cartItemId]
    );
  },
  []);

  const editCartItem = useCallback(
    function (cartId: string, cartItemId: string, fundCode: string) {
      const {
        transactionType,
        payload: { newPurchaseSipType },
      } = cartItems[0];
      const { path: editTransactionPath } =
        CART_ITEMS_MAP(newPurchaseSipType)[transactionType];
      history.push(editTransactionPath, { cartId, cartItemId, amc: fundCode });
    },
    [history, cartItems]
  );

  function handleNewPurchase() {
    const {
      transactionType,
      payload: { newPurchaseSipType },
      fund,
    } = cartItems[0];
    const requestBody = {
      cartId: cartItems[0].cartId,
      arnCode,
      pan,
      selectedCartItems: selected,
    };
    history.replace("/new-investor-transaction/investor-details", {
      requestBody,
      isCart: true,
      pan,
      transactionType,
      selectedCartItems: selected,
      newPurchaseSipType,
      fund,
      cartItems,
    });
  }

  function handleAdditionalSipPurchase() {
    // @ts-ignore
    const {
      pan,
      fund,
      payload: { folio },
      transactionType,
    }: CartItems<"ISIP">[number] = cartItems[0];

    const requestBody = {
      cartId: cartItems[0].cartId,
      arnCode,
      pan,
      selectedCartItems: selected,
    };
    history.replace("/cart/payment-options", {
      requestBody,
      pan,
      fund,
      folio,
      transactionType,
      isCart: true,
    });
  }

  const checkoutCart = useCallback(
    async function () {
      try {
        const { transactionType } = cartItems[0];
        if (
          transactionType === "SIN" &&
          cartItems[0].payload.newPurchaseSipType === "SIPN"
        ) {
          handleNewPurchase();
          return;
        }
        if (["NPL", "SINI"].includes(transactionType)) {
          handleNewPurchase();
          return;
        }

        if (Object.keys(PAYMENT_OPTIONS_MAP).includes(transactionType)) {
          // @ts-ignore
          history.replace(PAYMENT_OPTIONS_MAP[transactionType], {
            requestBody: {
              cartId: cartItems[0].cartId,
              arnCode,
              pan,
              selectedCartItems: selected,
            },
          });
          return;
        }

        if (transactionType === "ISIP" || transactionType === "SIN") {
          handleAdditionalSipPurchase();
          return;
        }
        const requestBody = {
          cartId: cartItems[0].cartId,
          arnCode,
          pan,
          selectedCartItems: selected,
        };
        const response = await cartCheckout(requestBody)(dispatch);
        if (response) {
          const { batchId } = response;
          alert(
            `Transaction submitted successfully with batch number ${batchId}`
          );
          history.replace("/new-transaction");
        }
      } catch (error) {
        handleError(error);
      }
    },
    [arnCode, cartItems, dispatch, history, pan, selected]
  );

  const addNewTransaction = useCallback(
    function () {
      const {
        transactionType,
        payload: { newPurchaseSipType },
      } = cartItems[0];

      const { newTransactionPath } =
        CART_ITEMS_MAP(newPurchaseSipType)[transactionType];
      history.push(newTransactionPath);
    },
    [history, cartItems]
  );

  const getFundDesc = useCallback(
    function (fundCode: string): string {
      const fund = funds.find(({ value }) => value.toString() === fundCode);
      return fund?.label || "";
    },
    [funds]
  );

  const mappedCartItems = useMemo(
    () => mapCartItemsToFundCodes(cartItems),
    [cartItems]
  );

  const fundCodes = Object.keys(mappedCartItems);

  useEffect(() => {
    (async function () {
      setFunds(await getAmcMasters()(dispatch));
    })();
  }, [dispatch]);

  useEffect(() => {
    setSelected(cartItems.map((item) => item.cartItemId));
  }, [cartItems]);

  const transactionName =
    cartItems[0].payload.newPurchaseSipType === "SIPN"
      ? "SIP New"
      : CART_ITEMS_MAP()[cartItems[0].transactionType].trName;

  const isKYCInvalid = cartItems.every((item) => item.payload.isKYCInvalid);
  const message = cartItems.find((item) => item.payload.kycMessage)?.payload
    ?.kycMessage;

  return {
    pan,
    fundCodes,
    getFundDesc,
    investorName: cartItems.length > 0 ? cartItems[0].investorName : "-",
    selectCartItem,
    removeCartItem,
    addNewTransaction,
    mappedCartItems,
    transactionType: cartItems?.[0]?.transactionType,
    transactionName:
      transactionName ||
      CART_ITEMS_MAP()[cartItems?.[0]?.transactionType]?.trName,
    checkoutCart,
    editCartItem,
    cartItems,
    selected,
    isKYCInvalid,
    message,
  };
}
